
import { Component, Vue } from 'vue-property-decorator'
import {
  getSmartMapUsers,
  getVoiceAssistantUsers,
  getWeAssistUsers,
  exportSmartMapUsersCsv,
  exportVoiceAssistantUsersCsv,
  exportWeAssistUsersCsv,
  getPurchaseAnalyticsData,
  getDeviceAnalyticsData,
  getDeviceReplacementsData
} from '@/api/analytics'
import request from '@/utils/request'
import moment from 'moment'
import XLSX from 'xlsx'
import { saveAs } from 'file-saver'

@Component({
  name: 'BusinessAnalytics'
})
export default class extends Vue {
  private activeTab = 'smart-map'

  // Export
  private showExportDialog = false
  private exportDateRange: string[] = []
  private exportGroupBy = 'month'
  private exportTabs: string[] = ['smart-map', 'voice-assistant', 'weassist']
  private exportLoading = false

  get exportDaySpan(): number {
    if (!this.exportDateRange || this.exportDateRange.length < 2) return 0
    const d1 = new Date(this.exportDateRange[0])
    const d2 = new Date(this.exportDateRange[1])
    return Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24))
  }

  // Smart Map
  private smartMapLoading = false
  private smartMapPage = 1
  private smartMapLimit = 20
  private smartMapSearch = ''
  private smartMapSearchTimer: any = null
  private smartMapExporting = false
  private smartMapStartDate = ''
  private smartMapEndDate = ''
  private smartMapData: any = { users: [], pagination: {}, summary: {} }
  private smartMapSourceFilters: string[] = []

  // Voice Assistant
  private vaSourceFilters: string[] = []
  private vaLoading = false
  private vaPage = 1
  private vaLimit = 20
  private vaSearch = ''
  private vaSearchTimer: any = null
  private vaExporting = false
  private vaStartDate = ''
  private vaEndDate = ''
  private vaData: any = { users: [], pagination: {}, summary: {} }

  // WeAssist
  private weassistLoading = false
  private weassistPage = 1
  private weassistLimit = 20
  private weassistSearch = ''
  private weassistSearchTimer: any = null
  private weassistExporting = false
  private weassistStartDate = ''
  private weassistEndDate = ''
  private weassistData: any = { users: [], pagination: {}, summary: {} }

  // Purchases
  private purchaseLoading = false
  private purchaseStartDate = ''
  private purchaseEndDate = ''
  private purchaseData: any = { summary: [], byProduct: [], byPlatform: [], timeSeries: [] }

  // Devices
  private deviceLoading = false
  private deviceStartDate = ''
  private deviceEndDate = ''
  private deviceData: any = { connected: [], activated: [], connectionTrend: [], activationTrend: [] }

  // Replacements
  private replacementLoading = false
  private replacementStartDate = ''
  private replacementEndDate = ''
  private replacementData: any = { summary: [], timeSeries: [], recentEvents: [] }

  // Device Detail
  private deviceDetailVisible = false
  private deviceDetailLoading = false
  private deviceDetailData: any = null
  private deviceFeatures: any[] = []
  private deviceFeaturesLoading = false

  get purchaseTotals() {
    const byPlatform = this.purchaseData.byPlatform || []
    return {
      total: byPlatform.reduce((s: number, p: any) => s + (p.total || 0), 0),
      newPurchases: byPlatform.reduce((s: number, p: any) => s + (p.new_purchases || 0), 0),
      renewals: byPlatform.reduce((s: number, p: any) => s + (p.renewals || 0), 0),
      revenue: byPlatform.reduce((s: number, p: any) => s + parseFloat(p.revenue || 0), 0),
    }
  }

  get connectedTotal() {
    return (this.deviceData.connected || []).reduce((s: number, d: any) => s + (d.count || 0), 0)
  }

  get activatedTotal() {
    return (this.deviceData.activated || []).reduce((s: number, d: any) => s + (d.count || 0), 0)
  }

  created() {
    this.loadSmartMapUsers()
  }

  private handleTabChange(tab: any) {
    const name = tab.name
    if (name === 'smart-map' && !this.smartMapData.users?.length) this.loadSmartMapUsers()
    if (name === 'voice-assistant' && !this.vaData.users?.length) this.loadVaUsers()
    if (name === 'weassist' && !this.weassistData.users?.length) this.loadWeassistUsers()
    if (name === 'purchases' && !this.purchaseData.byProduct?.length) this.loadPurchaseAnalytics()
    if (name === 'devices' && !this.deviceData.connected?.length) this.loadDeviceAnalytics()
    if (name === 'replacements' && !this.replacementData.recentEvents?.length) this.loadReplacements()
  }

  private onSmartMapSourceChange() {
    this.smartMapPage = 1
    this.loadSmartMapUsers()
  }

  private onVaSourceChange() {
    this.vaPage = 1
    this.loadVaUsers()
  }

  private fetchSmartMap() { this.loadSmartMapUsers() }
  private fetchVaUsers() { this.loadVaUsers() }

  private async loadSmartMapUsers() {
    this.smartMapLoading = true
    try {
      const source = this.smartMapSourceFilters.length > 0 ? this.smartMapSourceFilters.join(',') : undefined
      const { data } = await getSmartMapUsers(this.smartMapPage, this.smartMapLimit, this.smartMapSearch, this.smartMapStartDate, this.smartMapEndDate, source)
      this.smartMapData = data.data
    } catch (e: any) {
      if (e?.code !== 'ECONNABORTED') this.$message.error('Failed to load Smart Map users')
    } finally {
      this.smartMapLoading = false
    }
  }

  private handleSmartMapSearch() {
    if (this.smartMapSearchTimer) clearTimeout(this.smartMapSearchTimer)
    this.smartMapSearchTimer = setTimeout(() => {
      this.smartMapPage = 1
      this.loadSmartMapUsers()
    }, 500)
  }

  private handleSmartMapDateChange() {
    this.smartMapPage = 1
    this.loadSmartMapUsers()
  }

  private async exportSmartMap() {
    this.smartMapExporting = true
    try {
      const { data } = await exportSmartMapUsersCsv(this.smartMapSearch, this.smartMapStartDate, this.smartMapEndDate)
      const url = window.URL.createObjectURL(new Blob([data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `smart_map_users_${moment().format('YYYY-MM-DD')}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      this.$message.error('Failed to export')
    } finally {
      this.smartMapExporting = false
    }
  }

  // Voice Assistant
  private async loadVaUsers() {
    this.vaLoading = true
    try {
      const source = this.vaSourceFilters.length > 0 ? this.vaSourceFilters.join(',') : undefined
      const { data } = await getVoiceAssistantUsers(this.vaPage, this.vaLimit, this.vaSearch, this.vaStartDate, this.vaEndDate, source)
      this.vaData = data.data
    } catch (e: any) {
      if (e?.code !== 'ECONNABORTED') this.$message.error('Failed to load Voice Assistant users')
    } finally {
      this.vaLoading = false
    }
  }

  private handleVaSearch() {
    if (this.vaSearchTimer) clearTimeout(this.vaSearchTimer)
    this.vaSearchTimer = setTimeout(() => {
      this.vaPage = 1
      this.loadVaUsers()
    }, 500)
  }

  private handleVaDateChange() {
    this.vaPage = 1
    this.loadVaUsers()
  }

  private async exportVaUsers() {
    this.vaExporting = true
    try {
      const { data } = await exportVoiceAssistantUsersCsv(this.vaSearch, this.vaStartDate, this.vaEndDate)
      const url = window.URL.createObjectURL(new Blob([data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `voice_assistant_users_${moment().format('YYYY-MM-DD')}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      this.$message.error('Failed to export')
    } finally {
      this.vaExporting = false
    }
  }

  // WeAssist
  private async loadWeassistUsers() {
    this.weassistLoading = true
    try {
      const { data } = await getWeAssistUsers(this.weassistPage, this.weassistLimit, this.weassistSearch, this.weassistStartDate, this.weassistEndDate)
      this.weassistData = data.data
    } catch (e: any) {
      if (e?.code !== 'ECONNABORTED') this.$message.error('Failed to load WeAssist users')
    } finally {
      this.weassistLoading = false
    }
  }

  private handleWeassistSearch() {
    if (this.weassistSearchTimer) clearTimeout(this.weassistSearchTimer)
    this.weassistSearchTimer = setTimeout(() => {
      this.weassistPage = 1
      this.loadWeassistUsers()
    }, 500)
  }

  private handleWeassistDateChange() {
    this.weassistPage = 1
    this.loadWeassistUsers()
  }

  private async exportWeassistUsers() {
    this.weassistExporting = true
    try {
      const { data } = await exportWeAssistUsersCsv(this.weassistSearch, this.weassistStartDate, this.weassistEndDate)
      const url = window.URL.createObjectURL(new Blob([data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `weassist_users_${moment().format('YYYY-MM-DD')}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      this.$message.error('Failed to export')
    } finally {
      this.weassistExporting = false
    }
  }

  private async loadPurchaseAnalytics() {
    this.purchaseLoading = true
    try {
      const { data } = await getPurchaseAnalyticsData(this.purchaseStartDate, this.purchaseEndDate)
      this.purchaseData = data.data
    } catch (e: any) {
      if (e?.code !== 'ECONNABORTED') this.$message.error('Failed to load purchase analytics')
    } finally {
      this.purchaseLoading = false
    }
  }

  private async loadDeviceAnalytics() {
    this.deviceLoading = true
    try {
      const { data } = await getDeviceAnalyticsData(this.deviceStartDate, this.deviceEndDate)
      this.deviceData = data.data
    } catch (e: any) {
      if (e?.code !== 'ECONNABORTED') this.$message.error('Failed to load device analytics')
    } finally {
      this.deviceLoading = false
    }
  }

  private async loadReplacements() {
    this.replacementLoading = true
    try {
      const { data } = await getDeviceReplacementsData(this.replacementStartDate, this.replacementEndDate)
      this.replacementData = data.data
    } catch (e: any) {
      if (e?.code !== 'ECONNABORTED') this.$message.error('Failed to load replacement data')
    } finally {
      this.replacementLoading = false
    }
  }

  private handleSmartMapPageChange(page: number) {
    this.smartMapPage = page
    this.loadSmartMapUsers()
  }

  private handleSmartMapSizeChange(size: number) {
    this.smartMapLimit = size
    this.smartMapPage = 1
    this.loadSmartMapUsers()
  }

  private formatDate(date: string) {
    return date ? moment(date).format('DD.MM.YYYY HH:mm') : '-'
  }

  private formatDateShort(date: string) {
    return date ? moment(date).format('DD.MM.YYYY') : '-'
  }

  private formatSourceType(sourceType: string) {
    const map: Record<string, string> = {
      device_owner: 'Device Owner',
      device_connection: 'Device Connection',
      ios_purchaser: 'iOS Purchase',
      android_purchaser: 'Android Purchase',
      app_store_purchase: 'App Store',
      google_play_purchase: 'Google Play',
      admin_grant: 'Admin Grant',
      zoho_purchase: 'Zoho',
      campaign: 'Campaign',
      subscription_purchase: 'Subscription',
    }
    return map[sourceType] || sourceType
  }

  private async openDeviceDetail(serialNumber: string) {
    this.deviceDetailData = null
    this.deviceFeatures = []
    this.deviceDetailLoading = true
    this.deviceFeaturesLoading = true
    this.deviceDetailVisible = true
    try {
      const [deviceRes, featuresRes] = await Promise.all([
        request({ url: `/devices/${serialNumber}`, method: 'get' }),
        request({ url: `/devices/${serialNumber}/features`, method: 'get', params: { includeExpired: true } })
      ])
      this.deviceDetailData = deviceRes.data?.data || deviceRes.data
      this.deviceFeatures = featuresRes.data?.data || featuresRes.data || []
    } catch {
      this.deviceDetailData = null
      this.deviceFeatures = []
    } finally {
      this.deviceDetailLoading = false
      this.deviceFeaturesLoading = false
    }
  }

  private getSourceTagType(sourceType: string) {
    const map: Record<string, string> = {
      device_owner: 'success',
      device_connection: 'success',
      ios_purchaser: 'primary',
      android_purchaser: 'success',
      app_store_purchase: 'primary',
      google_play_purchase: 'success',
      admin_grant: 'warning',
      zoho_purchase: 'info',
      campaign: 'info',
    }
    return map[sourceType] || ''
  }

  private getEventTagType(eventType: string) {
    const map: Record<string, string> = {
      REPLACED: 'warning',
      REFUNDED: 'danger',
      REPAIR_STARTED: 'warning',
      REPAIR_COMPLETED: 'success',
    }
    return map[eventType] || 'info'
  }

  private getStatusColor(status: string) {
    const map: Record<string, string> = {
      REPLACED: '#e6a23c',
      REFUNDED: '#f56c6c',
      REPAIR: '#909399',
    }
    return map[status] || '#303133'
  }

  private getStatusCount(status: string) {
    return (this.replacementData.summary || [])
      .filter((s: any) => s.lifecycle_status === status)
      .reduce((sum: number, s: any) => sum + (s.count || 0), 0)
  }

  private getStatusByType(status: string) {
    return (this.replacementData.summary || []).filter((s: any) => s.lifecycle_status === status)
  }

  private async exportToExcel() {
    if (!this.exportDateRange || this.exportDateRange.length < 2) return
    this.exportLoading = true
    try {
      const wb = XLSX.utils.book_new()
      const startDate = this.exportDateRange[0]
      const endDate = this.exportDateRange[1]

      if (this.exportTabs.includes('smart-map')) {
        const { data } = await getSmartMapUsers(1, 10000, '', startDate, endDate)
        const users = data.data?.users || []
        const rows = users.map((u: any) => ({
          Name: [u.first_name, u.last_name].filter(Boolean).join(' '),
          Email: u.email,
          Phone: u.phone_number,
          Source: this.formatSourceType(u.real_source || u.source_type),
          Device: u.connected_device_serial || '',
          'Device Type': u.device_type || '',
          'Start Date': u.start_date?.substring(0, 10),
          'End Date': u.end_date?.substring(0, 10) || 'Unlimited',
        }))
        const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Name: 'No data' }])
        XLSX.utils.book_append_sheet(wb, ws, 'Smart Map Users')
      }

      if (this.exportTabs.includes('voice-assistant')) {
        const { data } = await getVoiceAssistantUsers(1, 10000, '', startDate, endDate)
        const users = data.data?.users || []
        const rows = users.map((u: any) => ({
          Name: [u.first_name, u.last_name].filter(Boolean).join(' '),
          Email: u.email,
          Phone: u.phone_number,
          Source: this.formatSourceType(u.real_source || u.source_type),
          Device: u.connected_device_serial || '',
          'Start Date': u.start_date?.substring(0, 10),
          'End Date': u.end_date?.substring(0, 10) || 'Unlimited',
        }))
        const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Name: 'No data' }])
        XLSX.utils.book_append_sheet(wb, ws, 'Voice Assistant Users')
      }

      if (this.exportTabs.includes('weassist')) {
        const { data } = await getWeAssistUsers(1, 10000, '', startDate, endDate)
        const users = data.data?.users || []
        const rows = users.map((u: any) => ({
          Name: [u.first_name, u.last_name].filter(Boolean).join(' '),
          Email: u.email,
          Phone: u.phone_number,
          Platform: u.platform,
          Product: u.product_name,
          'Total Minutes': u.total_minutes,
          'Used Minutes': u.used_minutes,
          'Remaining': u.remaining_minutes,
          'Expires': u.expires_date?.substring(0, 10),
        }))
        const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Name: 'No data' }])
        XLSX.utils.book_append_sheet(wb, ws, 'WeAssist Users')
      }

      // Auto-width
      for (const name of wb.SheetNames) {
        const ws = wb.Sheets[name]
        const d = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1 })
        if (d.length) {
          ws['!cols'] = (d[0] as any[]).map((_: any, i: number) => {
            let max = 10
            for (const row of d as any[][]) { if (row[i] != null) max = Math.max(max, String(row[i]).length) }
            return { wch: Math.min(max + 2, 50) }
          })
        }
      }

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' })
      const buf = new ArrayBuffer(wbout.length)
      const view = new Uint8Array(buf)
      for (let i = 0; i < wbout.length; i++) view[i] = wbout.charCodeAt(i) & 0xFF

      saveAs(new Blob([buf], { type: 'application/octet-stream' }), `Business_Analytics_${startDate}_${endDate}.xlsx`)
      this.showExportDialog = false
    } catch (e) {
      console.error('Export failed:', e)
      this.$message.error('Export failed')
    }
    this.exportLoading = false
  }
}
